import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode, MutableRefObject } from 'react';
import type { BreadcrumbItem } from '../components/ui/Breadcrumbs/Breadcrumbs';

export interface PageChromeLabels {
  readonly title: string | null;
  readonly description: string | null;
  readonly breadcrumbs: readonly BreadcrumbItem[];
}

export interface PageChromeApi {
  readonly setLabels: (next: {
    title: string;
    description: string | null;
    breadcrumbs: readonly BreadcrumbItem[];
  }) => void;
  readonly clearLabels: () => void;
  readonly iconSlotRef: MutableRefObject<HTMLElement | null>;
  readonly actionsSlotRef: MutableRefObject<HTMLElement | null>;
}

const EMPTY: PageChromeLabels = {
  title: null,
  description: null,
  breadcrumbs: [],
};

function sameBreadcrumbs(a: readonly BreadcrumbItem[], b: readonly BreadcrumbItem[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((item, index) => item.label === b[index]?.label && item.to === b[index]?.to);
}

/** Header subscribes here — updates only when title/description change. */
const PageChromeLabelsContext = createContext<PageChromeLabels>(EMPTY);

/** PageContainer uses this — setters/refs are stable across label updates. */
const PageChromeApiContext = createContext<PageChromeApi | null>(null);

export interface PageChromeProviderProps {
  readonly children: ReactNode;
}

/**
 * Publishes page title/description into the shared header.
 *
 * Labels and API are separate contexts so publishing a title does not rebuild the
 * publisher identity (which would re-fire PageContainer's layout effect in a loop).
 * Icon/actions stay out of React state — they portal into header DOM slots.
 */
export function PageChromeProvider({ children }: PageChromeProviderProps): React.JSX.Element {
  const [labels, setLabelsState] = useState<PageChromeLabels>(EMPTY);
  const iconSlotRef = useRef<HTMLElement | null>(null);
  const actionsSlotRef = useRef<HTMLElement | null>(null);

  const setLabels = useCallback(
    (next: { title: string; description: string | null; breadcrumbs: readonly BreadcrumbItem[] }) => {
      setLabelsState((current) => {
        if (
          current.title === next.title &&
          current.description === next.description &&
          sameBreadcrumbs(current.breadcrumbs, next.breadcrumbs)
        ) {
          return current;
        }
        return { title: next.title, description: next.description, breadcrumbs: next.breadcrumbs };
      });
    },
    []
  );

  const clearLabels = useCallback(() => {
    setLabelsState((current) =>
      current.title === null && current.description === null ? current : EMPTY
    );
  }, []);

  const api = useMemo<PageChromeApi>(
    () => ({
      setLabels,
      clearLabels,
      iconSlotRef,
      actionsSlotRef,
    }),
    [setLabels, clearLabels]
  );

  return (
    <PageChromeApiContext.Provider value={api}>
      <PageChromeLabelsContext.Provider value={labels}>{children}</PageChromeLabelsContext.Provider>
    </PageChromeApiContext.Provider>
  );
}

export function usePageChromeState(): PageChromeLabels {
  return useContext(PageChromeLabelsContext);
}

export function usePageChromePublisher(): PageChromeApi | null {
  return useContext(PageChromeApiContext);
}

export function usePageChromeSlots(): {
  readonly iconSlotRef: MutableRefObject<HTMLElement | null>;
  readonly actionsSlotRef: MutableRefObject<HTMLElement | null>;
} | null {
  const api = useContext(PageChromeApiContext);
  if (api === null) return null;
  return { iconSlotRef: api.iconSlotRef, actionsSlotRef: api.actionsSlotRef };
}
